<template>
  <div class="detail-view">
    <div class="detail-view-intro">
      <h1 class="detail-view-title">
        Detalhamento da importação · {{ agentName }}
      </h1>
    </div>

    <p v-if="loading" class="detail-view-loading">
      Carregando dados da importação…
    </p>

    <template v-else>
      <div class="detail-stats">
        <StatTile
          label="Encontradas no portal"
          :value="totals.encontradas"
          icon="mdi-cloud-search-outline"
          color="secondary"
          :hint="`${executions.length} execução(ões) do Pipeline no período`"
        />
        <StatTile
          label="Importadas no Phoenix"
          :value="totals.importadas"
          icon="mdi-tray-arrow-down"
          color="success"
          :badge="`${totals.sucessoRate}%`"
          hint="Entraram no Phoenix sem erro de job"
        />
        <StatTile
          label="Erros de importação"
          :value="totals.erro"
          icon="mdi-alert-circle-outline"
          color="error"
          :badge="`${totals.erroRate}%`"
          :hint="`${jobCount} job(s) com erro`"
        />
      </div>

      <section class="detail-section">
        <div class="detail-section-title">Do portal ao Phoenix</div>
        <div v-if="totals.encontradas" class="funnel">
          <div class="funnel-bar">
            <div
              class="funnel-seg funnel-seg--ok"
              :style="{ width: pct(totals.importadas) }"
              :title="`Importadas: ${totals.importadas}`"
            >
              <span v-if="segLabelVisible(totals.importadas)">{{
                totals.importadas
              }}</span>
            </div>
            <div
              class="funnel-seg funnel-seg--err"
              :style="{ width: pct(totals.erro) }"
              :title="`Erros: ${totals.erro}`"
            >
              <span v-if="segLabelVisible(totals.erro)">{{ totals.erro }}</span>
            </div>
          </div>
          <div class="funnel-legend">
            <span class="funnel-legend-item">
              <span class="dot dot--found"></span>
              {{ totals.encontradas }} encontradas
            </span>
            <span class="funnel-legend-item">
              <span class="dot dot--ok"></span>
              {{ totals.importadas }} importadas ({{ totals.sucessoRate }}%)
            </span>
            <span class="funnel-legend-item">
              <span class="dot dot--err"></span>
              {{ totals.erro }} com erro ({{ totals.erroRate }}%)
            </span>
          </div>
        </div>
        <p v-else class="detail-empty">
          Nenhuma reserva encontrada no portal neste período.
        </p>
      </section>

      <section class="detail-section">
        <div class="detail-section-title">Execuções do Pipeline no período</div>
        <div v-if="executions.length" class="detail-table-wrap">
          <table class="detail-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Janela</th>
                <th>Status</th>
                <th class="num">Encontradas</th>
                <th class="num">Importadas</th>
                <th class="num">Erros</th>
                <th class="num">Pendências</th>
                <th class="num">Taxa</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ex in executions" :key="ex.id">
                <td>{{ ex.date }}</td>
                <td class="muted">
                  {{ ex.start }}–{{ ex.end || "…" }} · {{ ex.duration }}
                </td>
                <td><StatusChip :status="ex.status" /></td>
                <td class="num">{{ ex.encontradas }}</td>
                <td class="num ok">{{ ex.importadas }}</td>
                <td class="num" :class="{ err: ex.erro > 0 }">{{ ex.erro }}</td>
                <td class="num">{{ ex.pendencias }}</td>
                <td class="num">
                  <span class="rate-pill" :class="rateClass(ex.sucessoRate)"
                    >{{ ex.sucessoRate }}%</span
                  >
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3">Total do período</td>
                <td class="num">{{ totals.encontradas }}</td>
                <td class="num ok">{{ totals.importadas }}</td>
                <td class="num" :class="{ err: totals.erro > 0 }">
                  {{ totals.erro }}
                </td>
                <td class="num">{{ totals.pendencias }}</td>
                <td class="num">
                  <span class="rate-pill" :class="rateClass(totals.sucessoRate)"
                    >{{ totals.sucessoRate }}%</span
                  >
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p v-else class="detail-empty">
          Nenhuma execução do Pipeline neste período.
        </p>
      </section>

      <ListaRecolhivel titulo="Reservas que ficaram de fora (erro de importação)" :total="erros.length">
        <div v-if="erros.length" class="detail-table-wrap">
          <table class="detail-table import-error-table">
            <colgroup>
              <col class="col-voucher" />
              <col class="col-motivo" />
              <col class="col-job" />
              <col class="col-detectada" />
            </colgroup>
            <thead>
              <tr>
                <th>Voucher</th>
                <th>Motivo do erro</th>
                <th>Job</th>
                <th>Detectada</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in erros" :key="e.voucher">
                <td class="voucher-cell">{{ e.voucher }}</td>
                <td>{{ e.motivo }}</td>
                <td class="muted job-cell">{{ e.grupo }}</td>
                <td class="muted">{{ e.detectadaEm }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          Nenhum erro de importação no período. Todas as reservas encontradas
          entraram no Phoenix.
        </div>
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas importadas sem pendência" :total="semPendenciaMeta.total">
        <div v-if="semPendencia.length" class="detail-table-wrap">
          <table class="detail-table import-ok-table">
            <colgroup>
              <col class="col-chevron" />
              <col class="col-voucher" />
              <col class="col-exec" />
              <col class="col-importada" />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Voucher</th>
                <th>Execução</th>
                <th>Importada</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="i in semPendencia">
                <tr
                  :key="i.voucher"
                  class="import-row"
                  :class="{ 'is-open': openVoucher === i.voucher }"
                  @click="toggleVoucher(i.voucher)"
                >
                  <td class="chevron-cell">
                    <v-icon
                      size="18"
                      class="chevron"
                      :class="{ 'is-open': openVoucher === i.voucher }"
                      >mdi-chevron-right</v-icon
                    >
                  </td>
                  <td class="voucher-cell">
                    {{ i.voucher }}
                    <v-icon
                      v-if="i.telefone"
                      size="15"
                      color="#0f7a6e"
                      class="phone-badge"
                      :title="`Telefone: ${i.telefone}`"
                      >mdi-phone</v-icon
                    >
                  </td>
                  <td class="muted">{{ execLabel(i) }}</td>
                  <td class="muted">{{ i.importadaEm }}</td>
                </tr>
                <tr
                  v-if="openVoucher === i.voucher"
                  :key="i.voucher + '-log'"
                  class="import-detail-row"
                >
                  <td colspan="4">
                    <div class="import-detail-log">
                      <p v-if="logLoading" class="detail-view-loading">
                        Carregando…
                      </p>
                      <p
                        v-else-if="!voucherLogsFor(i.voucher).length"
                        class="detail-empty"
                      >
                        Nenhum evento registrado para esse voucher.
                      </p>
                      <LogTimeline v-else :logs="voucherLogsFor(i.voucher)" />
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot v-if="semPendenciaMeta.listadas < semPendenciaMeta.total">
              <tr>
                <td colspan="4">
                  Listando {{ semPendenciaMeta.listadas }} de
                  {{ semPendenciaMeta.total }} no período.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p v-else class="detail-empty">
          Nenhuma reserva importada sem pendência no período.
        </p>
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas que ficaram em pendência" :total="comPendenciaMeta.total">
        <div v-if="comPendencia.length" class="detail-table-wrap">
          <table class="detail-table import-pendencia-table">
            <colgroup>
              <col class="col-chevron" />
              <col class="col-voucher" />
              <col class="col-status" />
              <col class="col-motivo" />
              <col class="col-detectada" />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Voucher / Reserva</th>
                <th>Status</th>
                <th>Motivo</th>
                <th>Detectada</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="p in comPendencia">
                <tr
                  :key="p.voucher"
                  class="import-row"
                  :class="{ 'is-open': openVoucher === p.voucher }"
                  @click="toggleVoucher(p.voucher)"
                >
                  <td class="chevron-cell">
                    <v-icon
                      size="18"
                      class="chevron"
                      :class="{ 'is-open': openVoucher === p.voucher }"
                      >mdi-chevron-right</v-icon
                    >
                  </td>
                  <td class="voucher-cell">
                    {{ p.voucher }}
                    <span v-if="p.recCode" class="muted">
                      · {{ p.recCode }}</span
                    >
                    <v-icon
                      v-if="p.telefone"
                      size="15"
                      color="#0f7a6e"
                      class="phone-badge"
                      :title="`Telefone: ${p.telefone}`"
                      >mdi-phone</v-icon
                    >
                  </td>
                  <td>
                    <span
                      class="rate-pill"
                      :class="pendStatusClass(p.status)"
                      >{{ pendStatusLabel(p.status) }}</span
                    >
                  </td>
                  <td>{{ p.motivo || "—" }}</td>
                  <td class="muted">{{ p.detectadaEm }}</td>
                </tr>
                <tr
                  v-if="openVoucher === p.voucher"
                  :key="p.voucher + '-log'"
                  class="import-detail-row"
                >
                  <td colspan="5">
                    <div class="import-detail-log">
                      <p v-if="logLoading" class="detail-view-loading">
                        Carregando…
                      </p>
                      <p
                        v-else-if="!voucherLogsFor(p.voucher).length"
                        class="detail-empty"
                      >
                        Nenhum evento registrado para esse voucher.
                      </p>
                      <LogTimeline v-else :logs="voucherLogsFor(p.voucher)" />
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot v-if="comPendenciaMeta.listadas < comPendenciaMeta.total">
              <tr>
                <td colspan="5">
                  Listando {{ comPendenciaMeta.listadas }} de
                  {{ comPendenciaMeta.total }} no período.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          Nenhuma reserva ficou em pendência no período.
        </div>
      </ListaRecolhivel>

      <ListaRecolhivel
        titulo="Código de reserva já existe no Phoenix com outro voucher"
        :total="codigoExistenteMeta.total"
      >
        <CodigoExistente
          :items="codigoExistente"
          :meta="codigoExistenteMeta"
        />
      </ListaRecolhivel>

      <ListaRecolhivel
        titulo="Apelidos de hotel cadastrados pelo agente"
        :total="apelidosCriadosMeta.total"
      >
        <ApelidosHotel
          tipo="criados"
          :items="apelidosCriados"
          :meta="apelidosCriadosMeta"
        />
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas com os mesmos passageiros (para ligar)" :total="duplicadasMeta.total">
        <PossiveisDuplicadas :items="duplicadas" :meta="duplicadasMeta" />
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas editadas no portal da CVC" :total="editadasMeta.total">
        <PortalAlteracoes
          tipo="editada"
          :items="editadas"
          :meta="editadasMeta"
        />
      </ListaRecolhivel>

      <ListaRecolhivel titulo="Reservas canceladas no portal da CVC" :total="canceladasMeta.total">
        <PortalAlteracoes
          tipo="cancelada"
          :items="canceladas"
          :meta="canceladasMeta"
        />
      </ListaRecolhivel>
    </template>
  </div>
</template>

<script>
import { getImportDetail, getVoucherLogs } from "@/services/nexiaApi.js";
import StatTile from "@/components/StatTile/StatTile.vue";
import StatusChip from "@/components/StatusChip/StatusChip.vue";
import LogTimeline from "@/views/Dashboard/Partials/LogTimeline/LogTimeline.vue";
import PortalAlteracoes from "./PortalAlteracoes.vue";
import PossiveisDuplicadas from "./PossiveisDuplicadas.vue";
import CodigoExistente from "./CodigoExistente.vue";
import ApelidosHotel from "./ApelidosHotel.vue";
import ListaRecolhivel from "@/components/ListaRecolhivel/ListaRecolhivel.vue";

const EMPTY = {
  totals: {
    encontradas: 0,
    importadas: 0,
    erro: 0,
    pendencias: 0,
    sucessoRate: 0,
    erroRate: 0,
  },
  executions: [],
  erros: [],
  semPendencia: [],
  semPendenciaMeta: { total: 0, listadas: 0 },
  comPendencia: [],
  comPendenciaMeta: { total: 0, listadas: 0 },
  canceladas: [],
  canceladasMeta: { total: 0, listadas: 0 },
  editadas: [],
  editadasMeta: { total: 0, listadas: 0 },
  duplicadas: [],
  duplicadasMeta: { total: 0, listadas: 0 },
  codigoExistente: [],
  codigoExistenteMeta: { total: 0, listadas: 0 },
  apelidosCriados: [],
  apelidosCriadosMeta: { total: 0, listadas: 0 },
};

const PEND_STATUS_LABEL = {
  aguardando: "Aguardando",
  resolvida: "Resolvida",
  partial: "Parcial",
  resolvida_reimport: "Resolvida (reimport.)",
};

export default {
  name: "ImportDetail",
  components: {
    StatTile,
    StatusChip,
    LogTimeline,
    PortalAlteracoes,
    PossiveisDuplicadas,
    CodigoExistente,
    ApelidosHotel,
    ListaRecolhivel,
  },
  props: {
    range: { type: Object, required: true },
    agent: { type: String, default: null },
    agentName: { type: String, default: "" },
    base: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      totals: EMPTY.totals,
      executions: EMPTY.executions,
      erros: EMPTY.erros,
      semPendencia: EMPTY.semPendencia,
      semPendenciaMeta: EMPTY.semPendenciaMeta,
      comPendencia: EMPTY.comPendencia,
      comPendenciaMeta: EMPTY.comPendenciaMeta,
      canceladas: EMPTY.canceladas,
      canceladasMeta: EMPTY.canceladasMeta,
      editadas: EMPTY.editadas,
      editadasMeta: EMPTY.editadasMeta,
      duplicadas: EMPTY.duplicadas,
      duplicadasMeta: EMPTY.duplicadasMeta,
      codigoExistente: EMPTY.codigoExistente,
      codigoExistenteMeta: EMPTY.codigoExistenteMeta,
      apelidosCriados: EMPTY.apelidosCriados,
      apelidosCriadosMeta: EMPTY.apelidosCriadosMeta,
      openVoucher: null,
      logLoading: false,
      voucherLogs: {},
    };
  },
  computed: {
    jobCount() {
      return new Set(this.erros.map((e) => e.grupo)).size;
    },
  },
  watch: {
    range() {
      this.load();
    },
    agent() {
      this.load();
    },
    base() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.openVoucher = null;
      this.voucherLogs = {};
      try {
        const data = await getImportDetail(this.range, this.agent, this.base);
        this.totals = data.totals;
        this.executions = data.executions;
        this.erros = data.erros;
        this.semPendencia = data.semPendencia || [];
        this.semPendenciaMeta = data.semPendenciaMeta || EMPTY.semPendenciaMeta;
        this.comPendencia = data.comPendencia || [];
        this.comPendenciaMeta = data.comPendenciaMeta || EMPTY.comPendenciaMeta;
        this.canceladas = data.canceladas || [];
        this.canceladasMeta = data.canceladasMeta || EMPTY.canceladasMeta;
        this.editadas = data.editadas || [];
        this.editadasMeta = data.editadasMeta || EMPTY.editadasMeta;
        this.duplicadas = data.duplicadas || [];
        this.duplicadasMeta = data.duplicadasMeta || EMPTY.duplicadasMeta;
        this.codigoExistente = data.codigoExistente || [];
        this.codigoExistenteMeta = data.codigoExistenteMeta || EMPTY.codigoExistenteMeta;
        this.apelidosCriados = data.apelidosCriados || [];
        this.apelidosCriadosMeta = data.apelidosCriadosMeta || EMPTY.apelidosCriadosMeta;
      } catch (err) {
        console.error("Falha ao carregar detalhamento de importação", err);
        this.totals = EMPTY.totals;
        this.executions = [];
        this.erros = [];
        this.semPendencia = [];
        this.semPendenciaMeta = EMPTY.semPendenciaMeta;
        this.comPendencia = [];
        this.comPendenciaMeta = EMPTY.comPendenciaMeta;
        this.canceladas = [];
        this.canceladasMeta = EMPTY.canceladasMeta;
        this.editadas = [];
        this.editadasMeta = EMPTY.editadasMeta;
        this.duplicadas = [];
        this.duplicadasMeta = EMPTY.duplicadasMeta;
        this.codigoExistente = [];
        this.codigoExistenteMeta = EMPTY.codigoExistenteMeta;
        this.apelidosCriados = [];
        this.apelidosCriadosMeta = EMPTY.apelidosCriadosMeta;
      } finally {
        this.loading = false;
      }
    },
    execLabel(i) {
      const ex = this.executions.find((e) => e.id === i.execId);
      if (!ex) return i.execDate || "—";
      const janela = ex.end ? `${ex.start}–${ex.end}` : ex.start;
      return `${ex.date} · ${janela}`;
    },
    pct(n) {
      if (!this.totals.encontradas) return "0%";
      return `${(n / this.totals.encontradas) * 100}%`;
    },
    segLabelVisible(n) {
      return this.totals.encontradas && n / this.totals.encontradas > 0.06;
    },
    rateClass(rate) {
      if (rate >= 95) return "rate-pill--ok";
      if (rate >= 80) return "rate-pill--warn";
      return "rate-pill--bad";
    },
    pendStatusLabel(status) {
      return PEND_STATUS_LABEL[status] || status;
    },
    pendStatusClass(status) {
      if (status === "resolvida" || status === "resolvida_reimport")
        return "rate-pill--ok";
      if (status === "partial") return "rate-pill--warn";
      return "rate-pill--bad";
    },
    voucherLogsFor(voucher) {
      return this.voucherLogs[voucher] || [];
    },
    async toggleVoucher(voucher) {
      if (this.openVoucher === voucher) {
        this.openVoucher = null;
        return;
      }
      this.openVoucher = voucher;
      if (this.voucherLogs[voucher]) return;
      this.logLoading = true;
      try {
        const data = await getVoucherLogs(voucher, this.base);
        this.$set(
          this.voucherLogs,
          voucher,
          (data.logs || []).map((l) => ({ time: l.time, text: l.text }))
        );
      } catch (err) {
        console.error("Falha ao carregar histórico do voucher", err);
        this.$set(this.voucherLogs, voucher, []);
      } finally {
        this.logLoading = false;
      }
    },
  },
};
</script>

<style src="./styles.scss" scoped lang="scss" />
